import React, { useMemo } from 'react';
import { Select } from 'antd';
import type { ReportParameter } from '../../types';

export interface StaticSelectProps {
  param: ReportParameter;
  disabled?: boolean;
  placeholder?: string;
  mode?: 'multiple' | undefined;
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  onBlur?: () => void;
}

/**
 * Static select component for parameters that define their options inline in the XML config.
 * No backend API call — options come directly from param.options.
 *
 * Used for parameter types: SELECT (without optionsEndpoint), MULTI_SELECT
 *
 * The -1 sentinel value is treated as "All / no filter". It is stripped from the rendered
 * options list and the component renders with an empty selection and a placeholder of
 * "All <label>" so the user sees a meaningful hint instead of "-1".
 * When cleared, the value submitted to the backend is null (= no filter applied).
 */
const StaticSelect: React.FC<StaticSelectProps> = ({
  param,
  disabled,
  placeholder,
  mode,
  value,
  onChange,
  onBlur,
}) => {
  const options = useMemo(() => {
    return (param.options ?? [])
      .filter((opt) => opt.value !== '-1' && opt.value !== '-1')
      .map((opt) => ({
        value: opt.value,
        label: opt.label,
        disabled: opt.disabled,
      }));
  }, [param.options]);

  // Treat -1 sentinel as unselected (null = all)
  const normalizedValue = value === '-1' || (value as any) === -1 ? undefined : value;

  // Default placeholder: "All <Label>" so it's clear the field is optional
  const resolvedPlaceholder = placeholder ?? `All ${param.label}`;

  return (
    <Select
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      mode={mode}
      showSearch
      allowClear
      optionFilterProp="label"
      value={normalizedValue}
      onChange={onChange as any}
      onBlur={onBlur}
      options={options}
    />
  );
};

export default StaticSelect;
